import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Delete as DeleteIcon,
  Pencil as EditIcon,
  DotsVertical as DotsVerticalIcon,
  LightningBolt as LightningBoltIcon,
} from "@actinc/dls/icons/";
import {
  Avatar,
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Popover,
} from "@mui/material";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { TTSAccessibilityDialog } from "./TTSAccessibilityDialog";
import { useTTSAccessibilityTagsSelection } from "./hooks/useTTSAccessibilityTagsSelection";

import {
  applyTTSAccessibility,
  createTTSAccessibilityTag,
  deleteTextToSpeechTags,
  expandRangeToFindCommonNode,
  getCleanContent,
  getOrderedElements,
  getTextContent,
  getTextToSpeechTags,
  getTrimmedRange,
  getTTSOptions,
} from "../../services/contentUtils";
import { useMemoCompare } from "../../services/hooks";
import {
  getAssetFileName,
  getLetter,
  transformMathMl,
} from "../../services/utils";
import { useClient } from "urql";
import { Play as PlayIcon } from "@actinc/dls/icons";
import _ from "lodash/fp";
import { ControlledFormSwitch } from "../ControlledFormSwitch";
import {
  deriveTagStatus,
  getLiveEntryForTag,
  hasRetryableDbTags,
  isTerminalBatchStatus,
  TTS_DEBUG,
  useTTSFileStatusContext,
} from "../../providers/TTSFileStatusProvider";

const TTSAccessibilityPanel = ({ objectType, orientation, readonly }) => {
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [bulkActionsAnchorEl, setBulkActionsAnchorEl] = useState(null);
  const [contextMenuState, setContextMenuState] = useState({
    mouseX: null,
    mouseY: null,
    options: [],
    selection: null,
  });
  const [currentId, setCurrentId] = useState(null);
  const [dialogState, setDialogState] = useState({ open: false });

  const { control, getValues, setValue } = useFormContext();
  const { replace: replaceAccessibilityTags } = useFieldArray({
    control,
    name: `${objectType}.accessibilityElements`,
  });

  const { ttsTagStatusMap } = useTTSFileStatusContext();

  const audioRef = useRef(null);

  const [shouldPlay, setShouldPlay] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);

  const [content, ttsAccessibilityTags] = useMemoCompare(
    getTextToSpeechTags(objectType)(getValues(objectType)),
  );
  const interactions = useMemoCompare(
    objectType === "item"
      ? getValues(`item.interactions`).filter(
          ({ __typename }) => __typename === "ChoiceInteraction",
        )
      : [],
  );

  const {
    selectedTags,
    selectAll,
    handleSelectAllCheckBox,
    handleCheckBox,
    clearSelection,
  } = useTTSAccessibilityTagsSelection(ttsAccessibilityTags);

  const client = useClient();

  const closeBulkActionsAnchorEl = () => {
    setBulkActionsAnchorEl(null);
  };

  const closeActionsAnchorEl = () => {
    setActionsAnchorEl(null);
  };
  const contentElement = useMemo(() => {
    const element = document.createElement("div");
    element.innerHTML = content;
    return element;
  }, [content]);

  const contentElementRef = useRef();

  // This is called to apply all TTS.
  const applyTTS = () => {
    const obj = applyTTSAccessibility(objectType)(getValues(objectType));

    if (obj.accessibilityElements && obj.interactions) {
      obj.accessibilityElements = obj.accessibilityElements.map((tag) => {
        for (const interaction of obj.interactions) {
          if (!interaction.foils) continue;

          const foilIndex = interaction.foils.findIndex((foil) => {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = foil.content;
            const spanWithId = tempDiv.querySelector(
              `span[id="${tag.identifier}"]`,
            );
            return spanWithId !== null;
          });

          if (foilIndex !== -1) {
            const foilLetter = getLetter(foilIndex);
            const currentText = tag?.accessibilityFeatures?.[0]?.text || "";
            const prefix = `${foilLetter}; `;

            if (currentText && !currentText.startsWith(prefix)) {
              return {
                ...tag,
                accessibilityFeatures: [
                  {
                    ...tag.accessibilityFeatures[0],
                    text: prefix + currentText,
                  },
                ],
              };
            }
            return tag;
          }
        }
        return tag;
      });
    }

    setValue(objectType, obj, { shouldDirty: true });
    closeBulkActionsAnchorEl();
  };

  const closeContextMenu = () => {
    setContextMenuState({
      mouseX: null,
      mouseY: null,
      options: [],
      selection: null,
    });
  };

  const deleteSelectedTTSTags = () => {
    const newObj = deleteTextToSpeechTags(`${objectType}`)(
      getValues(`${objectType}`),
    )(selectedTags);

    setValue(`${objectType}`, newObj, { shouldDirty: true });
    clearSelection();
    closeBulkActionsAnchorEl();
  };

  const deleteTTSTag = () => {
    const newObj = deleteTextToSpeechTags(`${objectType}`)(
      getValues(`${objectType}`),
    )([currentId]);

    setValue(`${objectType}`, newObj, { shouldDirty: true });
    closeActionsAnchorEl();
    setCurrentId(null);
  };

  const editTTSTag = () => {
    openTTSTagDialog({
      tag: ttsAccessibilityTags.find((tag) => tag?.identifier === currentId),
    });
    closeActionsAnchorEl();
  };

  const playTTSTag = async (e) => {
    // Check if anchorEl is already set and contains the current target.
    if (anchorEl?.contains(e.currentTarget)) {
      setShouldPlay(false);
      closeActionsAnchorEl();
    } else {
      // Set the new anchor element and start playing
      setAnchorEl(e.currentTarget);
      setShouldPlay(true);
    }
  };

  const handleDialogClose = (option) => (data) => {
    setDialogState({ open: false });
    if (!data) return;
    if (!data.identifier) {
      const [tag, element, key] = createTTSAccessibilityTag({
        ...option,
        tag: data,
      });
      const orderedElements = getOrderedElements(element.outerHTML)(
        ".ttsAccessibilityElement",
      );

      const orderedAccessibilityTags = [...ttsAccessibilityTags, tag].sort(
        (a, b) =>
          orderedElements.findIndex(
            (el) => el.getAttribute("id") === a.identifier,
          ) -
          orderedElements.findIndex(
            (el) => el.getAttribute("id") === b.identifier,
          ),
      );
      replaceAccessibilityTags(orderedAccessibilityTags);
      setValue(key, getCleanContent(element), {
        shouldDirty: true,
      });
    } else {
      // edit tags
      replaceAccessibilityTags(
        ttsAccessibilityTags.map((tag) =>
          tag.identifier === data.identifier ? { ...tag, ...data } : { ...tag },
        ),
      );
    }
    closeActionsAnchorEl();
  };

  const handleMouseLeave = ({ identifier }) => {
    [...contentElementRef.current.querySelectorAll(`[id="${identifier}"]`)].map(
      (el) => el.classList.remove("active"),
    );
  };

  const handleMouseOver = ({ identifier }) => {
    [...contentElementRef.current.querySelectorAll(`[id="${identifier}"]`)].map(
      (el) => el.classList.add("active"),
    );
  };

  const handleMouseUp = (e) => {
    if (e.button !== 0) return;
    const selection = window.getSelection();

    const range = _.pipe([getTrimmedRange, expandRangeToFindCommonNode])(
      selection.getRangeAt(0),
    );
    setContextMenuState({
      ...contextMenuState,
      mouseX: e.clientX,
      mouseY: e.clientY,
      options: getTTSOptions(contentElementRef.current)(range),
    });
  };

  const openTTSTagDialog = async ({ tag, ...option }) => {
    const label = option.label;
    let accessibleText;
    switch (label) {
      case "MathML":
        accessibleText = await transformMathMl(client)(
          option.elements[0].outerHTML,
        );
        tag.accessibilityFeatures[0].text = accessibleText;
        break;

      case "Image":
        const imageAssets = getValues(`${objectType}.imageAssets`);
        // Match on filename rather than the full src path: cloned passages keep
        // the original passage's asset path in the <img> src, so a full-path
        // compare misses and the Long Description comes up blank (CDE-17327).
        const imageFileName = getAssetFileName(option.elements[0].id);
        accessibleText =
          imageFileName &&
          imageAssets.find((ia) => getAssetFileName(ia.src) === imageFileName);
        tag.accessibilityFeatures[0].text =
          accessibleText?.longDescription || accessibleText?.altText || "";
        break;

      default:
        break;
    }
    closeContextMenu();
    setDialogState({ open: true, option, readonly, tag });
  };

  const objectId = getValues(`${objectType}.id`);
  // useWatch (reactive) so the panel re-renders when ttsTagStatuses is
  // refreshed via setValue after a batch terminal — getValues alone is not
  // reactive and would leave the per-tag icons stale until next render.
  const dbStatuses = (
    useWatch({
      control,
      name: `${objectType}.ttsTagStatuses`,
    }) ?? []
  ).filter(Boolean);
  const filesStatusMap = ttsTagStatusMap[objectId] ?? {};
  const hasRetryableTags = useMemo(
    () =>
      hasRetryableDbTags(dbStatuses, filesStatusMap) ||
      Object.entries(filesStatusMap).some(
        ([k, v]) => k !== "__requestStatus" && v.status === "FAILED",
      ),
    [dbStatuses, filesStatusMap],
  );

  const batchRequestStatus = filesStatusMap["__requestStatus"]?.status;
  const batchIsProcessing = batchRequestStatus === "PROCESSING";
  // Once the Lambda emits any terminal __requestStatus, in-flight DB rows
  // without a corresponding per-tag event are stale (backend won't deliver
  // them). deriveTagStatus uses this to graduate stuck DB-processing tags to
  // STUCK so the user can retry them.
  const batchHasTerminated = isTerminalBatchStatus(batchRequestStatus);

  // While the batch is still PROCESSING and not every expected tag has
  // arrived, a per-tag FAILED is often corrected by a later GENERATED for the
  // same tag (backend retries individual tags and streams both attempts).
  // Treat such transient FAILEDs as "still in progress" so the panel doesn't
  // flash red on FAILED → GENERATED races during retry.
  const liveTagCount = Object.keys(filesStatusMap).filter(
    (k) => k !== "__requestStatus",
  ).length;
  const deferLiveFailed =
    batchIsProcessing && liveTagCount < ttsAccessibilityTags.length;

  // The cached /assets/{objectId}/{tagId}.mp3 was generated from the previous
  // valid text; if the latest generation FAILED/STUCK, playing it from the
  // 3-dots menu would mislead the user (the icon already shows the error).
  const currentTagStatus = useMemo(() => {
    if (!currentId) return null;
    const liveEntry = getLiveEntryForTag(filesStatusMap, currentId);
    const dbEntry = dbStatuses.find(({ tagId }) => tagId === currentId);
    return deriveTagStatus({
      liveEntry,
      dbEntry,
      deferLiveFailed,
      batchIsProcessing,
      batchHasTerminated,
    });
  }, [
    currentId,
    filesStatusMap,
    dbStatuses,
    deferLiveFailed,
    batchIsProcessing,
    batchHasTerminated,
  ]);
  const listenDisabled =
    !ttsAccessibilityTags.some((tag) => tag?.identifier === currentId) ||
    !!currentTagStatus?.isTagFailed ||
    !!currentTagStatus?.isTagStuck;

  useEffect(() => {
    if (!TTS_DEBUG || !ttsAccessibilityTags.length) return;

    let inProgress = 0;
    let failed = 0;
    let stuck = 0;
    let completed = 0;

    ttsAccessibilityTags.forEach((tag) => {
      const liveEntry = getLiveEntryForTag(filesStatusMap, tag.identifier);
      const dbEntry = dbStatuses.find(({ tagId }) => tagId === tag.identifier);
      const { isTagFailed, isTagStuck, isTagProcessing } = deriveTagStatus({
        liveEntry,
        dbEntry,
        deferLiveFailed,
        batchIsProcessing,
        batchHasTerminated,
      });

      if (isTagFailed) failed++;
      else if (isTagStuck) stuck++;
      else if (isTagProcessing) inProgress++;
      else completed++;
    });

    const total = ttsAccessibilityTags.length;
    console.info(
      `[TTS Tags - ${objectType}] ${inProgress} in progress - ${completed} completed - ${failed} failed - ${stuck} stuck` +
        ` (total: ${total}) | batchIsProcessing: ${batchIsProcessing}`,
    );
  }, [filesStatusMap, dbStatuses, batchIsProcessing]);

  return (
    <>
      <Box
        onMouseUp={(e) => !readonly && handleMouseUp(e)}
        ref={contentElementRef}
        sx={{
          border: "1px solid lightgrey",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "100%",
          overflowY: "auto",
          paddingX: 1,
          width: "100%",
          "& div.ttsAccessibilityElement, p.ttsAccessibilityElement": {
            backgroundColor: "#dce4ef !important",
          },
          "& th.perspectiveTitle, & th.perspectiveSpacer, & th.perspective ,& td.perspective, td.perspectiveSpacer":
            {
              textAlign: "left",
              verticalAlign: "middle",
              fontWeight: "normal",
              borderStyle: "solid",
              borderWidth: "1px",
              padding: "3px",
            },
          "& table.ttsAccessibilityElement": {
            backgroundColor: "#f1f1f1 !important",
          },
          "& tr.ttsAccessibilityElement": {
            backgroundColor: "#e7f4e4 !important",
          },
          "& td.ttsAccessibilityElement,th.ttsAccessibilityElement": {
            backgroundColor: "#fff1d2 !important",
          },
          "& span.ttsAccessibilityElement": {
            backgroundColor: "#e4e2e0 !important",
          },
          "& math.ttsAccessibilityElement": {
            backgroundColor: "#e9f2fb !important",
          },
          "& img.ttsAccessibilityElement": {
            backgroundColor: "#e4e2e0 !important",
          },
          "& .ttsAccessibilityElement.active, & .ttsAccessibilityElement.active *":
            {
              backgroundColor: "#ffff7b !important",
            },
          "& .interaction": {
            display: "none",
          },
        }}
      >
        <Box
          dangerouslySetInnerHTML={{
            __html:
              contentElement.querySelector(`#${objectType}-content`)
                ?.outerHTML || "",
          }}
        />
        {interactions.map(({ foils, identifier }, index) => (
          <List key={`${objectType}-interaction-${identifier}`}>
            {foils.map((_foil, i) => {
              const letter = getLetter(i);
              const selector = `${objectType}-interaction-${index}-foil-${i}-content`;
              const el = contentElement.querySelector(`#${selector}`);
              return (
                <ListItem key={selector}>
                  <ListItemAvatar>
                    <Avatar sx={{ height: 24, width: 24 }}>{letter}</Avatar>
                  </ListItemAvatar>
                  <ListItemText>
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: el?.outerHTML || "",
                      }}
                    />
                  </ListItemText>
                </ListItem>
              );
            })}
          </List>
        ))}
      </Box>
      {!!contextMenuState.options.length && (
        <Menu
          keepMounted
          open={contextMenuState.mouseY !== null}
          onClose={closeContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenuState.mouseX !== null && contextMenuState.mouseY !== null
              ? {
                  left: contextMenuState.mouseX,
                  top: contextMenuState.mouseY,
                }
              : undefined
          }
        >
          {contextMenuState.options.map((option) => (
            <MenuItem
              dense={true}
              key={option.label}
              onClick={() => openTTSTagDialog(option)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      )}

      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          height: "100%",
          maxHeight: orientation === "left-right" ? "100%" : 250,
          maxWidth: orientation === "left-right" ? 400 : "100%",
          minHeight: 250,
          minWidth: 400,
          overflowY: "auto",
        }}
      >
        <List
          dense={true}
          subheader={
            <ListSubheader
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              {!readonly && (
                <ListItemIcon>
                  <Checkbox
                    checked={selectAll}
                    edge="start"
                    onChange={handleSelectAllCheckBox}
                    aria-label="Select All"
                  />
                </ListItemIcon>
              )}
              <Box>Text to Speech Tags</Box>
              <IconButton
                aria-label="more"
                aria-controls="tts-tags-bulk-actions-menu"
                aria-expanded={bulkActionsAnchorEl ? "true" : undefined}
                aria-haspopup="true"
                disabled={readonly}
                id="tts-tags-bulk-actions"
                onClick={(e) => setBulkActionsAnchorEl(e.target)}
              >
                <DotsVerticalIcon />
              </IconButton>

              <Menu
                anchorEl={bulkActionsAnchorEl}
                anchorOrigin={{
                  horizontal: "right",
                  vertical: "bottom",
                }}
                id="tts-tags-bulk-actions-menu"
                MenuListProps={{
                  "aria-labelledby": "tts-tags-bulk-actions",
                }}
                onClose={closeBulkActionsAnchorEl}
                open={Boolean(bulkActionsAnchorEl)}
                transformOrigin={{
                  horizontal: "right",
                  vertical: "top",
                }}
              >
                <MenuItem>
                  <ControlledFormSwitch
                    control={control}
                    fullWidth={true}
                    defaultValue={false}
                    id={`${objectType}.generateTTS`}
                    name={`${objectType}.generateTTS`}
                    key={`${objectType}.generateTTS`}
                    label={hasRetryableTags ? "Retry TTS" : "Generate TTS"}
                  />
                </MenuItem>
                <MenuItem onClick={applyTTS}>
                  <ListItemIcon>
                    <LightningBoltIcon size="small" />
                  </ListItemIcon>
                  <ListItemText>Automatically Apply TTS</ListItemText>
                </MenuItem>
                <MenuItem onClick={deleteSelectedTTSTags}>
                  <ListItemIcon>
                    <DeleteIcon size="small" />
                  </ListItemIcon>
                  <ListItemText>Delete Selected Tags</ListItemText>
                </MenuItem>
              </Menu>
            </ListSubheader>
          }
          sx={{
            backgroundColor: "background.paper",
            flexDirection: "column",
            flexGrow: 1,
            overflowX: "hidden",
            overflowY: "auto",
            paddingTop: 0,
            position: "relative",
          }}
        >
          {!!ttsAccessibilityTags &&
            ttsAccessibilityTags.map((tag, index) => {
              const elements = [
                ...contentElement.querySelectorAll(
                  `[id="${tag?.identifier}"], [aria-describedby="${tag?.identifier}"]`,
                ),
              ];
              const text =
                getTextContent(elements) === ""
                  ? "Image"
                  : getTextContent(elements);

              const liveEntry = getLiveEntryForTag(
                filesStatusMap,
                tag.identifier,
              );
              const dbEntry = dbStatuses.find(
                ({ tagId }) => tagId === tag.identifier,
              );
              const { isTagFailed, isTagStuck, isTagProcessing } =
                deriveTagStatus({
                  liveEntry,
                  dbEntry,
                  deferLiveFailed,
                  batchIsProcessing,
                  batchHasTerminated,
                });

              return (
                <ListItem
                  key={tag.identifier}
                  onMouseEnter={() => handleMouseOver(tag)}
                  onMouseLeave={() => handleMouseLeave(tag)}
                  sx={{ borderTop: "1px solid lightgrey" }}
                >
                  {!readonly && (
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedTags.includes(tag.identifier)}
                        edge="start"
                        onChange={handleCheckBox}
                        value={tag.identifier}
                      />
                    </ListItemIcon>
                  )}
                  <Tooltip aria-label={text} title={text}>
                    <ListItemText
                      disableTypography={true}
                      secondary={
                        <Box
                          sx={{
                            alignItems: "center",
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                          }}
                        >
                          {isTagFailed && (
                            <Tooltip title="TTS generation failed for this tag">
                              <ErrorIcon
                                sx={{
                                  color: "#f70404",
                                  fontSize: "1rem",
                                  mr: 0.5,
                                }}
                              />
                            </Tooltip>
                          )}
                          {isTagStuck && (
                            <Tooltip title="TTS generation did not complete — use Retry TTS to regenerate">
                              <WarningIcon
                                sx={{
                                  color: "#ff9800",
                                  fontSize: "1rem",
                                  mr: 0.5,
                                }}
                              />
                            </Tooltip>
                          )}
                          {isTagProcessing && (
                            <Tooltip title="TTS generation in progress">
                              <CircularProgress size={14} sx={{ mr: 0.5 }} />
                            </Tooltip>
                          )}
                          {!!elements.length && (
                            <Typography
                              noWrap
                              sx={{ lineHeight: "1.5em" }}
                              variant="body2"
                            >
                              {text}
                            </Typography>
                          )}
                        </Box>
                      }
                    ></ListItemText>
                  </Tooltip>
                  <ListItemSecondaryAction
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <IconButton
                      aria-label="more"
                      aria-controls={`tts-tag-${index}-actions-menu`}
                      aria-expanded={actionsAnchorEl ? "true" : undefined}
                      aria-haspopup="true"
                      id={`tts-tag-${index}-actions`}
                      onClick={(e) => {
                        setCurrentId(tag.identifier);
                        setActionsAnchorEl(e.target);
                      }}
                      size="small"
                    >
                      <DotsVerticalIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
        </List>
      </Box>

      <Menu
        anchorEl={actionsAnchorEl}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        id={`tts-tag-actions-menu`}
        MenuListProps={{
          "aria-labelledby": `tts-tag-actions`,
        }}
        onClose={closeActionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
      >
        {!readonly && (
          <MenuItem onClick={deleteTTSTag}>
            <ListItemIcon>
              <DeleteIcon size="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={editTTSTag}>
          <ListItemIcon>
            <EditIcon size="small" />
          </ListItemIcon>
          <ListItemText>{readonly ? "View" : "Edit"}</ListItemText>
        </MenuItem>
        <MenuItem disabled={listenDisabled} onClick={playTTSTag}>
          <ListItemIcon>
            <PlayIcon size="small" />
          </ListItemIcon>
          <ListItemText>Listen</ListItemText>
          <>
            {shouldPlay && (
              <Popover
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                onClose={() => setShouldPlay(false)}
                id="playTextToSpeechAudio"
                open={shouldPlay}
              >
                <span>
                  <audio ref={audioRef} autoPlay controls>
                    <source
                      src={`/assets/${getValues(
                        `${objectType}.id`,
                      )}/${currentId}.mp3`}
                      type="audio/mpeg"
                    />
                  </audio>
                </span>
              </Popover>
            )}
          </>
        </MenuItem>
      </Menu>
      {dialogState.open && (
        <TTSAccessibilityDialog
          handleClose={handleDialogClose}
          {...dialogState}
        />
      )}
    </>
  );
};

export { TTSAccessibilityPanel };
